/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { borderWidth, margin, padding, tint } from 'polished';

import playerSwapSelectedPlayerSelector from 'app/selectors/playerSwapSelectedPlayerSelector';
import Responsive from 'app/components/responsive/Responsive';
import PlayerSwapInfo from './PlayerSwapInfo';
import { getFactoredEntityPositionSalary } from '../../selectors/salaryExpenditureSelector';

type Props = {
  className?: string,
  danger?: boolean,
  isRacing?: boolean,
  message?: string,
  onCancel: () => void,
  onSubmit: () => void,
  selectedPlayer: Object,
  selectedPosition?: string,
  selectedPositionAbbr?: string,
  swapPlayer?: Object
};

const defaultProps = {
  className: undefined,
  danger: false,
  isRacing: false,
  message: undefined,
  selectedPosition: undefined,
  selectedPositionAbbr: undefined,
  swapPlayer: undefined
};

const Wrapper = styled.div(({ theme }) => ({
  display: 'flex',
  paddingTop: '0.5rem',
  [theme.mediaQuery.tabletLandscapeAndBelow]: {
    borderColor: theme.color.primary,
    borderStyle: 'solid',
    ...borderWidth(0, 0, 2)
  },
  [theme.mediaQuery.desktopAndUp]: {
    backgroundColor: 'white',
    paddingBottom: '0.5rem',
    ...padding(null, '1rem')
  }
}));

const Detail = styled.div(({ theme }) => ({
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1,
  [theme.mediaQuery.tabletLandscapeAndBelow]: {
    backgroundColor: theme.color.basis6
  }
}));

const DetailInner = styled.div(({ theme }) => ({
  backgroundColor: theme.color.white,
  ...margin('0.5rem', '0.5rem', '0.5rem', 0),
  [theme.mediaQuery.desktopAndUp]: {
    ...margin('0.5rem', 0),
    background: 'none',
    borderWidth: 0
  }
}));

const Central = styled.div({
  display: 'flex',
  flexBasis: 'auto',
  flexGrow: 0,
  flexShrink: 0
});

const Confirmation = styled.div({
  margin: 'auto',
  ...padding(0, '1rem')
});

const ConfirmText = styled.p(({ danger, theme }) => ({
  color: danger ? theme.color.danger : theme.color.basis4,
  textAlign: 'center'
}));

const ConfirmButton = styled('button', {
  withProps: {
    type: 'button'
  }
})(
  ({ theme }) => ({
    backgroundColor: theme.color.success,
    borderRadius: theme.borderRadius.medium,
    color: 'white',
    ...padding('0.375rem', '1.5rem'),
    textAlign: 'center',
    textTransform: 'uppercase',
    width: '100%'
  }),
  ({ disabled, theme }) =>
    disabled
      ? {
          backgroundColor: '#ccc'
        }
      : {
          backgroundColor: theme.color.success,
          '&:hover': {
            backgroundColor: tint(0.75, theme.color.success)
          }
        }
);

const BackButton = styled('button')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.color.basis6,
  color: theme.color.black,
  display: 'flex',
  flexBasis: '2rem',
  flexGrow: 0,
  flexShrink: 0,
  justifyContent: 'center'
}));

function PlayerSwapSummary({
  className,
  danger,
  isRacing,
  message,
  onCancel,
  onSubmit,
  selectedPlayer,
  selectedPosition,
  selectedPositionAbbr,
  swapPlayer
}: Props) {

  // !We don't need to multiply the salary by the factor because the out player is already multiplied in the backend
  const outPlayerFactoredSalary = selectedPlayer.salary;

  // ! This is not the case for the in player and we must multiply.
  const inPlayerFactoredSalary = swapPlayer
    ? getFactoredEntityPositionSalary(swapPlayer.salary, swapPlayer.positions, selectedPosition)
    : 0;

  return (
    <Wrapper className={className}>
      <Responsive mobile={!!selectedPlayer}>
        <BackButton type="button" onClick={onCancel}>
          <span className="ion-ios-arrow-back" />
        </BackButton>
      </Responsive>
      <Detail>
        <DetailInner>
          <PlayerSwapInfo
            average={selectedPlayer.average}
            avatar={isRacing ? selectedPlayer.riderImage : selectedPlayer.imageURI}
            awayTeam={selectedPlayer.awayAbbr}
            form={selectedPlayer.form}
            gameOrder={selectedPlayer.gameOrder}
            homeTeam={selectedPlayer.homeAbbr}
            initials={selectedPlayer.initials}
            isRacing={isRacing}
            isScratched={selectedPlayer.isScratched}
            labels={selectedPlayer.labels}
            isHome={selectedPlayer.isHome}
            name={selectedPlayer.name}
            place={selectedPlayer.placeProjected}
            position={selectedPositionAbbr}
            rider={selectedPlayer.rider}
            salary={outPlayerFactoredSalary}
            trainer={selectedPlayer.trainer}
            win={selectedPlayer.winProjected}
          />
        </DetailInner>
      </Detail>
      <Responsive desktop>
        <Central>
          <Confirmation>
            <ConfirmText danger={danger}>{message}</ConfirmText>
            <ConfirmButton disabled={!swapPlayer} onClick={onSubmit}>
              Confirm swap
            </ConfirmButton>
          </Confirmation>
        </Central>
      </Responsive>
      <Responsive desktop>
        <Detail>
          {swapPlayer && (
            <DetailInner>
              <PlayerSwapInfo
                layout="reverse"
                average={swapPlayer.average}
                avatar={isRacing ? swapPlayer.riderImage : swapPlayer.imageURI}
                awayTeam={swapPlayer.awayAbbr}
                form={swapPlayer.form}
                gameOrder={swapPlayer.gameOrder}
                homeTeam={swapPlayer.homeAbbr}
                initials={swapPlayer.initials}
                isRacing={isRacing}
                isScratched={swapPlayer.isScratched}
                labels={swapPlayer.labels}
                isHome={swapPlayer.isHome}
                name={swapPlayer.name}
                place={swapPlayer.placeProjected}
                position={selectedPositionAbbr}
                rider={swapPlayer.rider}
                salary={inPlayerFactoredSalary}
                trainer={swapPlayer.trainer}
                win={swapPlayer.winProjected}
              />
            </DetailInner>
          )}
        </Detail>
      </Responsive>
    </Wrapper>
  );
}

PlayerSwapSummary.defaultProps = defaultProps;

const connector = connect(state => {
  const { selectedPosition, selectedPositionAbbr, swapPlayerId } = state.playerSwap;

  return {
    selectedPlayer: playerSwapSelectedPlayerSelector(state),
    selectedPosition,
    selectedPositionAbbr,
    swapPlayer: state.playerSwap.playerPool[swapPlayerId]
  };
});

export default connector(PlayerSwapSummary);
